import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Group,
  Text,
  Title,
  Loader,
  Center,
  Stack,
} from "@mantine/core";
import { IconFileText } from "@tabler/icons-react";
import type { SortingState } from "@tanstack/react-table";
import { Payment } from "../../utils/interfaces";
import { DataTable, getSortedRowData, useDataTable } from "../table";
import { createCustomerInvoicesColumns } from "./customerInvoicesColumns";

const DEFAULT_SORT: SortingState = [{ id: "due_date", desc: false }];

interface CustomerInvoicesTableProps {
  payments: Payment[] | undefined;
  isLoading: boolean;
  isFetching?: boolean;
  showPaidInvoices: boolean;
  onShowPaidInvoicesChange: (next: boolean) => void;
  onOpenInvoice?: (externalId: string) => void;
}

export const CustomerInvoicesTable = ({
  payments,
  isLoading,
  isFetching = false,
  showPaidInvoices,
  onShowPaidInvoicesChange,
  onOpenInvoice,
}: CustomerInvoicesTableProps) => {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORT);
  const rows = payments ?? [];
  const columns = useMemo(
    () => createCustomerInvoicesColumns({ showStatus: showPaidInvoices }),
    [showPaidInvoices],
  );

  const table = useDataTable({
    data: rows,
    columns,
    getRowId: (row) => String(row.id),
    sorting,
    onSortingChange: setSorting,
  });

  const displayRows = getSortedRowData(table);

  const handleInvoiceRowClick = (payment: Payment) => {
    if (onOpenInvoice) {
      onOpenInvoice(payment.external_id);
      return;
    }
    navigate(`/invoice/${payment.external_id}`, {
      state: { payment },
    });
  };

  const tableBodyClassName =
    "max-h-[min(50vh,32rem)] min-h-48 overflow-x-auto overflow-y-auto rounded-lg border border-gray-200";

  return (
    <Paper p="md" shadow="sm" radius="md" withBorder className="bg-white">
      <Group justify="space-between" mb="md">
        <Title order={3} fw={700} className="text-gray-800">
          Invoices
        </Title>
        <div className="flex items-center gap-4">
          <label
            className="inline-flex items-center gap-2 text-sm text-slate-700"
            onClick={(event) => event.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={showPaidInvoices}
              onChange={(event) =>
                onShowPaidInvoicesChange(event.target.checked)
              }
              className="rounded border-gray-300"
            />
            Show paid invoices
            {isFetching ? <Loader size="xs" /> : null}
          </label>
          <Text size="sm" c="dimmed" fw={500}>
            {rows.length} {rows.length === 1 ? "invoice" : "invoices"}
          </Text>
        </div>
      </Group>

      {isLoading ? (
        <Center py="xl" className="min-h-48 rounded-lg border border-gray-200">
          <Loader size="lg" />
        </Center>
      ) : rows.length === 0 ? (
        <Center py="xl" className="min-h-48 rounded-lg border border-gray-200">
          <Stack align="center" gap="md">
            <IconFileText size={48} className="text-gray-300" />
            <Text c="dimmed" size="lg" fw={500}>
              No invoices found for this customer
            </Text>
          </Stack>
        </Center>
      ) : (
        <div className={tableBodyClassName}>
          <DataTable
            table={table}
            displayRows={displayRows}
            onRowClick={handleInvoiceRowClick}
            headerRowClassName="sticky top-0 z-10 bg-linear-to-r from-gray-50 to-gray-100 border-b border-gray-200 text-xs font-semibold uppercase tracking-wider text-gray-700"
            rowClassName="cursor-pointer bg-white transition-all duration-150 hover:bg-blue-50 hover:shadow-sm"
          />
        </div>
      )}
    </Paper>
  );
};
